/* eslint-disable import/extensions */
import { plugin as VueInputAutowidth } from 'vue-input-autowidth'
import { addBreadcrumb, captureException } from '@sentry/browser'
import { createApp } from 'vue'
import moment from 'moment'
import Toast from 'vue-toastification'
import { createPinia, setActivePinia } from 'pinia'
import { loadVueBaseComponents } from '@/app-bootstrapping/LoadVueBaseComponents'
import { loadVueComponents } from '@/app-bootstrapping/LoadVueComponents'
import { loadWdsComponents } from '@/app-bootstrapping/LoadWdsComponents'
import { setUpRouter } from '@/app-bootstrapping/SetUpRouter'
import { SET_EDIT_STATE, UPDATE_USER_STATE } from '@/state/mutation-types'
import { setUpStore } from '@/app-bootstrapping/SetUpStore'
import { initializeSentry, attachAppToSentry } from '@/utils/Sentry'
import { setUpInternationalization } from '@/app-bootstrapping/SetUpInternationalization'
import { loadVueDirectives } from '@/app-bootstrapping/LoadVueDirectives'
import GlobalAppMixin from '@/mixins/GlobalAppMixin'
import { provideApollo, setUpApollo } from '@/app-bootstrapping/SetUpApollo'
import { QueryEncoder } from '@/utils/Encoders'
import '@/legacy/bootstrap'
import '@/legacy/theme'
import '@/legacy/frontend/app'

// Add custom current variables leaked to the frontend in this declaration to fix typescript issues.
declare global {
  interface Window {
    currentAccountOwner: any
    currentAccount: any
    analytics: any
  }
}

const errorReportingEnabled =
  typeof import.meta.env.VITE_APP_ENV !== 'undefined' &&
  import.meta.env.VITE_APP_ENV !== 'testing' &&
  import.meta.env.VITE_APP_ENV !== 'local' &&
  import.meta.env.VITE_APP_ENV !== 'development'

moment.locale(currentLanguageSelected)
;(window as any).moment = moment

try {
  if (errorReportingEnabled) {
    initializeSentry({
      dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
      environment: import.meta.env.VITE_APP_ENV,
      release: import.meta.env.VITE_APP_VERSION, // Comes from vite.config.js
    })
  }

  /*
   * In some cases users don't have markets but can still access the site,
   * ie byo freelancers, so here set the market for them here
   */
  if (currentUser.markets && currentUser.markets.length === 0) {
    currentUser.markets = [currentMarket]
  }

  // Companies sometimes usually don't have more than one market. So we add one here
  if (
    currentAccount !== '' &&
    (!currentAccount.markets || (currentAccount.markets && currentAccount.markets.length === 0))
  ) {
    currentAccount.markets = [currentMarket]
  }

  const baseCreateApp = (configuration) => {
    const app = createApp(configuration)
    if (errorReportingEnabled) {
      attachAppToSentry(app)
    }
    setUpRouter(app)
    app.mixin(GlobalAppMixin)
    app.use(VueInputAutowidth)
    app.use(Toast, { transition: 'Vue-Toastification__fade' })
    app.config.compilerOptions.whitespace = 'preserve' // We want to keep any whitespace we have in .vue components
    app.config.globalProperties.window = window // make window object available globally for use as props value for example
    setUpInternationalization(app) // when using ts, use @composables/translation rather than using the mixin
    loadVueDirectives(app)
    setUpApollo(app)
    setUpStore(app)
    loadVueBaseComponents(app)
    loadVueComponents(app)
    loadWdsComponents(app)

    // VueJs devtool has issues detecting Pinia if usage is not last
    // see issue https://github.com/vuejs/devtools/issues/1839
    const pinia = createPinia()
    app.use(pinia)
    setActivePinia(pinia)
    return app
  }
  ;(window as any).VueApp = function instantiateVueApp(
    el = '#vueApp', // accept string or dom node element
    data = () => {},
    methods = {},
    computed = {},
    created = () => {},
    watch = () => {},
  ) {
    // Some places the vue components are loaded in via ajax and blade files,
    // therefore we might some times load the app manually from a blade file.
    // Sometimes we also might need to load more than one app, or an app with
    // a different id
    const domNodeEl = typeof el === 'string' ? document.getElementById(el.substr(1)) : el

    if (domNodeEl === null) return null
    const elementDesc = `${domNodeEl.id ? `id:${domNodeEl.id}` : ''} ${
      domNodeEl.className ? `class:${domNodeEl.className}` : ''
    } ${domNodeEl.outerHTML ? `html:${domNodeEl.outerHTML.slice(0, 40)}` : ''}`
    const elementData = {
      html: domNodeEl.outerHTML.replace(/^\s+|\s+$/g, ''),
      parent: domNodeEl.parentElement?.outerHTML.slice(0, 40),
    }
    if (errorReportingEnabled) {
      addBreadcrumb({
        message: `Setting up Vue app ${elementDesc}`,
        level: 'info',
        category: 'Vue',
        data: elementData,
      })
    }
    const dataOpt = typeof data === 'function' ? data : () => data
    const app = baseCreateApp({
      name: domNodeEl.id,
      data: dataOpt,
      computed,
      setup() {
        provideApollo()
      },
      created,
      beforeCreate() {
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'id',
          fieldValue: currentUser.id,
        })
        // We still have to use currentAccount here since this.account will be undefined at this point
        if (
          currentAccount.automatic_currency_conversion !== undefined &&
          currentAccount.has_seen_foreign_currency !== undefined
        ) {
          this.$store.commit(UPDATE_USER_STATE, {
            fieldName: 'automatic_currency_conversion',
            fieldValue: !!currentAccount.automatic_currency_conversion,
          })
          this.$store.commit(UPDATE_USER_STATE, {
            fieldName: 'has_seen_foreign_currency',
            fieldValue: !!currentAccount.has_seen_foreign_currency,
          })
        }
      },
      mounted() {
        if (QueryEncoder.decodeQueryIntoObject(window.location.search).edit !== undefined) {
          this.$store.commit(
            SET_EDIT_STATE,
            QueryEncoder.decodeQueryIntoObject(window.location.search).edit,
          )
        }
      },
      methods,
      watch,
    })
    ;(window as any)[`VueInstance${domNodeEl.id}`] = app
    app.mount(domNodeEl)
    if (errorReportingEnabled) {
      addBreadcrumb({
        message: `Initialized Vue app ${elementDesc}`,
        level: 'info',
        category: 'Vue',
      })
    }
    return app
  }

  // This is used to mount a fully specified vue app from blade files.
  // This is due to changes to Vue not allowing old blade vue apps to be mounted on their own.
  ;(window as any).mountApp = function mountApp(el, appOpts) {
    const domNodeEl = typeof el === 'string' ? document.getElementById(el.substr(1)) : el

    if (domNodeEl === null) return null
    const elementDesc = `${domNodeEl.id ? `id:${domNodeEl.id}` : ''} ${
      domNodeEl.class ? `class:${domNodeEl.class}` : ''
    } ${domNodeEl.outerHTML ? `html:${domNodeEl.outerHTML.slice(0, 40)}` : ''}`
    const elementData = {
      html: domNodeEl.outerHTML.replace(/^\s+|\s+$/g, ''),
      parent: domNodeEl.parentNode.outerHTML.slice(0, 40),
    }
    if (errorReportingEnabled) {
      addBreadcrumb({
        message: `Setting up Vue app ${elementDesc}`,
        level: 'info',
        category: 'Vue',
        data: elementData,
      })
    }
    const app = baseCreateApp({
      setup() {
        provideApollo()
      },
      ...appOpts,
    })
    app.mount(domNodeEl)
    if (errorReportingEnabled) {
      addBreadcrumb({
        message: `Initialized Vue app ${elementDesc}`,
        level: 'info',
        category: 'Vue',
      })
    }
    return app
  }

  if (document.getElementById('vueApp') != null) {
    ;(window as any).VueApp()
  }
  Array.from(document.querySelectorAll('[data-vue-app]')).forEach((node) =>
    (window as any).VueApp(node),
  )
  if (errorReportingEnabled) {
    addBreadcrumb({
      message: 'Finished initializing App.js',
      level: 'info',
      category: 'Vue',
    })
  }
} catch (e) {
  if (errorReportingEnabled) {
    captureException(e)
  } else {
    throw e
  }
}
